import { baseService } from './baseService'
import { authService } from './authService'
import type { Project, ProjectParticipant } from './projectTypes'

export interface Question {
  id: number
  unique_number: number
  text: string
  question_type: 'TEXT' | 'SINGLE_CHOICE' | 'MULTIPLE_CHOICE'
  section_name: string
  choices: Array<{
    value: number
    text: string
    skip_questions?: number
  }>
}

export interface QuestionnaireData {
  template_name: string
  instructions: string | null
  questions: Question[]
}

export interface QuestionnaireAnswer {
  question_id: number
  choices?: number[]
  note?: string
  message?: string
  start_time?: string
  show_answers_time?: string
  response_time?: string
  events?: any
}

export interface QuestionnaireResponse {
  completed_at: string
  answers: Array<QuestionnaireAnswer & { question: Question }>
}

/** A project as seen from the respondent panel, carrying the viewer's own
 *  participant context so the panel can launch the real capture journey. */
export type AssignedProject = Project & {
  /** Per-recipient token; launching `/r/<share_token>?invite=<invite_token>`
   *  attributes the run to this respondent's existing participant row. */
  invite_token?: string
  participant_status?: string
  questionnaire_completed?: boolean
}

export class ParticipantViewService {
  async getMyProjects(): Promise<AssignedProject[]> {
    const response = await baseService.get<AssignedProject[]>('/participant/projects/')
    return response.data
  }

  async getProjectDetail(projectId: number): Promise<AssignedProject & { questionnaire_started: boolean }> {
    const response = await baseService.get<AssignedProject & { questionnaire_started: boolean }>(
      `/participant/projects/${projectId}/`
    )
    return response.data
  }

  /** Ensure the current user has a tracked participant row for this study
   *  (creating one for a public study on first start) and get the tokens to
   *  launch the capture journey attributed to their account. */
  async joinStudy(projectId: number): Promise<{ invite_token: string; share_token: string }> {
    const response = await baseService.post<{ invite_token: string; share_token: string }>(
      `/participant/projects/${projectId}/join/`,
      {},
    )
    return response.data
  }

  async getProjectQuestionnaire(projectId: number): Promise<QuestionnaireData> {
    const response = await baseService.get<QuestionnaireData>(
      `/participant/projects/${projectId}/questionnaire/`
    )
    return response.data
  }

  async submitQuestionnaire(projectId: number, answers: QuestionnaireAnswer[]): Promise<{ message: string; answers_count: number }> {
    const response = await baseService.post<{ message: string; answers_count: number }>(
      `/participant/projects/${projectId}/submit_questionnaire/`,
      { answers }
    )
    return response.data
  }

  async getMyParticipantInfo(projectId: number): Promise<ProjectParticipant> {
    const user = await authService.getCurrentUser()
    const response = await baseService.get<ProjectParticipant>(
      `/project-participants/?project=${projectId}&participant=${user?.id}`
    )
    return response.data
  }

  async getParticipantResponse(projectId: number, participantId: number): Promise<QuestionnaireResponse> {
    const response = await baseService.get<QuestionnaireResponse>(
      `/participant/projects/${projectId}/participants/${participantId}/responses/`
    )
    return response.data
  }
}

export const participantViewService = new ParticipantViewService()

